import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { UserTokenWithSecret } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button, Card } from '../../components/ui';
import { errorMessage } from '../../components/ErrorPanel';
import { getOffice, loadOffice, saveToken } from './office';
import type { OfficeApi } from './office';

/**
 * Opened by the add-in in an Office dialog (ADR 0033). The user signs in to the portal as usual, agrees, and a user token
 * for Outlook (purpose `outlook`, ADR 0027) is created and handed back to the task pane with `messageParent`.
 */
export function OutlookConnectPage() {
  const [office, setOffice] = useState<OfficeApi | null>(null);
  useEffect(() => {
    let live = true;
    void loadOffice().then((o) => {
      if (live) setOffice(o);
    });
    return () => {
      live = false;
    };
  }, []);

  const create = useMutation({
    mutationFn: () => api.post('/me/tokens', { body: { purpose: 'outlook', label: t('w30.connect.label') }, schema: UserTokenWithSecret }),
    onSuccess: async (r) => {
      const o = office ?? getOffice() ?? (await loadOffice());
      if (o) o.context.ui.messageParent(r.token);
      else saveToken(null, r.token); // opened outside Outlook: keep it for this browser only
    },
  });

  return (
    <div className="mx-auto max-w-lg p-4 sm:p-6">
      <Card title={t('w30.connect.title')} description={t('w30.connect.description')}>
        <div className="space-y-4">
          {create.error ? <Alert>{errorMessage(create.error) || t('w30.connect.error')}</Alert> : null}
          {create.isSuccess ? (
            <Alert kind="success">{t('w30.connect.done')}</Alert>
          ) : (
            <>
              <p className="text-sm text-slate-600">{t('w30.connect.allowHint')}</p>
              <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
                {t('w30.connect.allow')}
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
